import { z } from 'zod';

import { mongoIdSchema } from './shared';

export enum OrganizationIntegrationError {
  AccessDenied = 'ACCESS_DENIED',
  DecryptionFailed = 'DECRYPTION_FAILED',
  EncryptionFailed = 'ENCRYPTION_FAILED',
  EncryptionKeyMissing = 'ENCRYPTION_KEY_MISSING',
  ExistingIntegrationNotFound = 'EXISTING_INTEGRATION_NOT_FOUND',
  FailedToExchangeCodeForToken = 'FAILED_TO_EXCHANGE_CODE_FOR_TOKEN',
  FailedRefresh = 'FAILED_REFRESH',
  GscAccessExpiredNoRefreshToken = 'GSC_ACCESS_EXPIRED_NO_REFRESH_TOKEN',
  GscFailedRefresh = 'GSC_FAILED_REFRESH',
  GscFailedToExchangeCodeForToken = 'GSC_FAILED_TO_EXCHANGE_CODE_FOR_TOKEN',
  GscInvalidTokenResponseMissingAccessToken = 'GSC_INVALID_TOKEN_RESPONSE_MISSING_ACCESS_TOKEN',
  GscNetworkErrorExchangingCodeForToken = 'GSC_NETWORK_ERROR_EXCHANGING_CODE_FOR_TOKEN',
  GscSearchAnalyticsQueryFailed = 'GSC_SEARCH_ANALYTICS_QUERY_FAILED',
  GscSitesListFailed = 'GSC_SITES_LIST_FAILED',
  IntegrationDoesNotBelongToOrganization = 'INTEGRATION_DOES_NOT_BELONG_TO_ORGANIZATION',
  IntegrationMissingCredentials = 'INTEGRATION_MISSING_CREDENTIALS',
  InvalidCredentials = 'INVALID_CREDENTIALS',
  NetworkError = 'NETWORK_ERROR',
  IntegrationNotActive = 'INTEGRATION_NOT_ACTIVE',
  IntegrationNotFound = 'INTEGRATION_NOT_FOUND',
  IntegrationNotFoundAfterUpdate = 'INTEGRATION_NOT_FOUND_AFTER_UPDATE',
  InvalidEncryptionKey = 'INVALID_ENCRYPTION_KEY',
  InvalidHmac = 'INVALID_HMAC',
  InvalidName = 'INVALID_NAME',
  InvalidOauthUrl = 'INVALID_OAUTH_URL',
  InvalidState = 'INVALID_STATE',
  InvalidTimestamp = 'INVALID_TIMESTAMP',
  InvalidTokenResponseMissingAccessToken = 'INVALID_TOKEN_RESPONSE_MISSING_ACCESS_TOKEN',
  InvalidToken = 'INVALID_TOKEN',
  InvalidUrl = 'INVALID_URL',
  MissingCode = 'MISSING_CODE',
  InsufficientScope = 'INSUFFICIENT_SCOPE',
  MissingHmac = 'MISSING_HMAC',
  NameRequired = 'NAME_REQUIRED',
  NetworkErrorExchangingCodeForToken = 'NETWORK_ERROR_EXCHANGING_CODE_FOR_TOKEN',
  TimestampExpired = 'TIMESTAMP_EXPIRED',
  UrlRequired = 'URL_REQUIRED',
  NotReachable = 'NOT_REACHABLE',
}

// Generic external URL schema — accepts real domains and localhost (for local dev).
// Single source of truth; used in route schemas and the create schema below.
export const externalUrlSchema = z
  .string()
  .min(1, OrganizationIntegrationError.UrlRequired)
  .regex(
    /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,63}|localhost)(:\d{2,5})?(\/[^\s]*)?$/,
    OrganizationIntegrationError.InvalidUrl
  );

// Shopify-specific URL schema — single source of truth for store URL validation
export const shopifyExternalUrlSchema = z
  .string()
  .min(1, OrganizationIntegrationError.UrlRequired)
  .regex(/^[a-zA-Z0-9-]+\.myshopify\.com$/, OrganizationIntegrationError.InvalidUrl);

// Enum for integration providers (extensible for future integrations)
export enum IntegrationProvider {
  Shopify = 'shopify',
  Wordpress = 'wordpress',
  Bloggle = 'bloggle',
  GoogleSearchConsole = 'google_search_console',
}

// Enum for integration status
export enum IntegrationStatus {
  Pending = 'pending', // OAuth initiated, waiting for callback
  Active = 'active', // Successfully connected
  Failed = 'failed', // OAuth failed or rejected
  Revoked = 'revoked', // Integration was disconnected
}

// Create schema (for new integrations)
export const organizationIntegrationCreateSchema = z.object({
  organizationId: mongoIdSchema,
  provider: z.nativeEnum(IntegrationProvider),
  status: z.nativeEnum(IntegrationStatus).default(IntegrationStatus.Pending),

  externalName: z.string().optional(),
  externalUrl: externalUrlSchema.optional(),

  // Access key can be:
  // - Encrypted access token (only after OAuth completes)
  // - Integration secret
  accessKey: z.string().optional(),
  refreshAccessKey: z.string().optional(), // Refresh token (e.g. Google OAuth)
  accessExpiresAt: z.number().optional(), // Token expiration (e.g. Shopify OAuth)
  accessUser: z.string().optional(),

  oauthState: z.string().optional(), // OAuth state parameter for CSRF protection
  oauthStateExpiresAt: z.number().optional(), // State expiration timestamp
  scope: z.string().optional(), // Granted scopes (only after OAuth completes)
});

// Full schema (with _id)
export const organizationIntegrationSchema = z
  .object({
    _id: mongoIdSchema,
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .merge(organizationIntegrationCreateSchema)
  // used to remove extras that we might need internally but not externally
  // for example: isArchived
  .strip();

export type OrganizationIntegration = z.infer<typeof organizationIntegrationSchema>;
export type OrganizationIntegrationCreate = z.infer<typeof organizationIntegrationCreateSchema>;
